import { isValidElement, useState } from 'react'
import type { JSX } from 'react'
import { DayPicker } from 'react-day-picker'
import type {
  ButtonProps,
  CaptionLabelProps,
  ChevronProps,
  MonthCaptionProps,
} from 'react-day-picker'

import { addDays, differenceInCalendarDays, subDays } from 'date-fns'

import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Flex,
  Heading,
  IconButton,
  InfoIcon,
  Label,
  Popover,
  Tooltip,
} from '@buffer-mono/popcorn'

import { formatComparisonDateRange, primaryRangePresetLabel } from '../../utils'

import dayPickerStyles from '~publish/components/DateRangePicker/DateRangePicker.module.css'

import styles from './ComparisonStartDatePicker.module.css'

const MonthCaption = ({
  children,
  className,
}: MonthCaptionProps): JSX.Element => (
  <Flex className={className} justify="start" align="center" gap="xs">
    {children}
  </Flex>
)

const CaptionLabel = (props: CaptionLabelProps): JSX.Element => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { children, ref, color, ...rest } = props
  return <Label {...rest}>{children}</Label>
}

const Chevron = ({
  className,
  size,
  orientation,
  disabled,
}: ChevronProps): JSX.Element => {
  const color = disabled ? 'disabled' : undefined
  return orientation === 'left' ? (
    <ChevronLeftIcon className={className} size={size} color={color} />
  ) : (
    <ChevronRightIcon className={className} size={size} color={color} />
  )
}

const NavigationButton = ({
  children,
  name,
  ...buttonProps
}: ButtonProps): JSX.Element => {
  const label =
    name === 'previous-month' ? 'Go to previous month' : 'Go to next month'
  const icon = isValidElement(children) ? children : <span />
  return (
    <IconButton
      label={label}
      variant="tertiary"
      as="button"
      size="medium"
      {...buttonProps}
    >
      {icon}
    </IconButton>
  )
}

const Weekday = ({
  children,
  ...props
}: JSX.IntrinsicElements['th']): JSX.Element => (
  <th {...props}>
    <Label size="small" color="subtle">
      {typeof children === 'string' ? children[0] : children}
    </Label>
  </th>
)

interface ComparisonStartDatePickerProps {
  /** Start date of the primary period — used to compute the comparison length. */
  primaryStartDate: Date
  /** End date of the primary period — used to compute the comparison length. */
  primaryEndDate: Date
  /** Previously-committed custom comparison start (drives the preselected day). */
  initialStartDate?: Date
  /**
   * Called when the user clicks Apply. The end date is auto-computed so the
   * comparison window length matches the primary period length.
   */
  onApply: (startDate: Date, endDate: Date) => void
  /** Called when the user clicks Cancel. */
  onCancel: () => void
  weekStartsOn?: 0 | 1
  /**
   * The primary period's selected preset (the `DateRangeToggle` value, e.g.
   * `last-7-days`). Drives the named selection shown in the summary; a custom
   * or omitted preset falls back to a computed day count.
   *
   * @see primaryRangePresetLabel
   */
  primaryRangePreset?: string
}

/**
 * Single-date picker for choosing a custom comparison START date.
 *
 * Renders as a `Popover.Content` so the caller composes the Popover shell
 * (open state + anchor) themselves. The comparison window length is forced
 * to match the primary period length: the user picks only the start and the
 * end is computed as `start + (primaryEnd − primaryStart)` calendar days, so
 * sum-based metrics remain directly comparable.
 */
export function ComparisonStartDatePicker({
  primaryStartDate,
  primaryEndDate,
  initialStartDate,
  onApply,
  onCancel,
  weekStartsOn = 0,
  primaryRangePreset,
}: ComparisonStartDatePickerProps): JSX.Element {
  const periodLengthDays = differenceInCalendarDays(
    primaryEndDate,
    primaryStartDate,
  )

  // Latest start the user is allowed to pick: the comparison window
  // (`c + periodLength`) must end strictly before the primary period starts —
  // so `c <= primaryStart − periodLength − 1` — and must not extend past
  // today. The smaller of the two caps wins.
  const today = new Date()
  const lastBeforePrimary = subDays(primaryStartDate, periodLengthDays + 1)
  const maxStartDate = today < lastBeforePrimary ? today : lastBeforePrimary

  const [pendingStart, setPendingStart] = useState<Date | undefined>(
    initialStartDate,
  )
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialStartDate ?? maxStartDate,
  )

  const handleApply = (): void => {
    if (!pendingStart) return
    const end = addDays(pendingStart, periodLengthDays)
    onApply(pendingStart, end)
  }

  // Duration shown in the summary: prefer the primary period's named preset
  // (e.g. "Last 7 days"), falling back to an inclusive day count for a custom
  // range (a 6-day calendar diff is a 7-day window, hence the +1).
  const inclusiveDayCount = periodLengthDays + 1
  const primaryDurationLabel = primaryRangePreset
    ? (primaryRangePresetLabel(primaryRangePreset) ??
      `${inclusiveDayCount} days`)
    : `${inclusiveDayCount} days`
  const primaryRangeLabel = formatComparisonDateRange(
    primaryStartDate,
    primaryEndDate,
    today,
  )
  const pendingEnd = pendingStart
    ? addDays(pendingStart, periodLengthDays)
    : null
  const comparisonRangeLabel =
    pendingStart && pendingEnd
      ? formatComparisonDateRange(pendingStart, pendingEnd, today)
      : '—'

  // react-day-picker's single mode applies the `selected` class to the
  // selected day's <td>. The shared DateRangePicker stylesheet only ships
  // `range_start / range_end / range_middle` (range mode), so without an
  // override the selected day has no styling. We map `selected` to a local
  // class that paints the green pill on the inner <button> only — avoiding
  // the range-tint band that would otherwise leak past the button when the
  // table cell is wider than the 32-px button.
  //
  // The `disabled` override layers a stronger, more obvious treatment on top
  // of the shared `[data-disabled='true']` rule — needed because the no-
  // overlap-with-primary constraint disables a contiguous block of dates.
  const dayPickerClassNames = {
    ...dayPickerStyles,
    selected: styles.selectedDay,
    disabled: styles.disabledDay,
  }

  return (
    <Popover.Content
      align="start"
      className={styles.content}
      // Outside-click (pointerdown) dismisses the popover normally. The
      // focus-outside path is fully suppressed because, on open, the
      // DropdownMenu restores focus to its trigger button — Radix interprets
      // that as focus leaving the popover and would dismiss it on the same
      // tick it appears. Keyboard users can still close via Escape, Cancel,
      // or Apply.
      onFocusOutside={(e): void => e.preventDefault()}
    >
      <div className={styles.intro}>
        <Heading size="xs" as="h2" className={styles.introTitle}>
          Comparison Start Date
        </Heading>
        <Tooltip content="We compare periods of equal length.">
          <span
            className={styles.infoTrigger}
            // The informational tooltip needs a focus target but does not perform an action.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label="Info"
          >
            <InfoIcon size="small" color="subtle" />
          </span>
        </Tooltip>
      </div>
      <div className={styles.body}>
        <DayPicker
          mode="single"
          numberOfMonths={1}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          weekStartsOn={weekStartsOn}
          disabled={[{ after: maxStartDate }]}
          selected={pendingStart}
          onSelect={setPendingStart}
          className={`${dayPickerStyles.root} ${styles.calendar}`}
          classNames={dayPickerClassNames}
          components={{
            CaptionLabel,
            Chevron,
            Weekday,
            Button: NavigationButton,
            MonthCaption,
          }}
        />
      </div>
      <div className={styles.summary} aria-live="polite">
        {primaryRangeLabel} ({primaryDurationLabel}) vs.{' '}
        <strong className={styles.summaryComparison}>
          {comparisonRangeLabel}
        </strong>
      </div>
      <div className={styles.footer}>
        <Button variant="tertiary" size="medium" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleApply}
          disabled={!pendingStart}
        >
          Apply
        </Button>
      </div>
    </Popover.Content>
  )
}
